import { useState } from 'react';
import { toast } from 'sonner';
import Helmet from '../lib/Helmet';
import { BUSINESS } from '../data/pricing';
import { track, FUNNEL } from '../lib/track';

// Route: /contact
//
// One place for every way to reach Citi Cool. Sales up front (77039 00307),
// service labelled on its own (95990 83770), the showroom with a map, and a
// short form for anyone who would rather be called back. The form posts to
// the same Google Form as the homepage, so enquiries land where they always
// have.

const SALES = { tel: '+917703900307', text: '77039 00307' };
const SERVICE = { tel: '+919599083770', text: '95990 83770' };
const EMAIL = 'admin@citicool.in';
const ADDRESS = ['13 Jain Mandir Market,', 'South Extension Part 2,', 'New Delhi 110049'];
const MAP_QUERY = 'Citi Cool Corporation, 13 Jain Mandir Market, South Extension Part 2, New Delhi 110049';
const FORM_URL = 'https://docs.google.com/forms/d/e/1FAIpQLScYHiUKJ4xt9EZhF_CLzHGnrTxV1UFQEh6a78r0WYzl2i5pGg/formResponse';
const NEEDS = ['New AC', 'VRV or project', 'Service and repairs', 'Clearance stock'];

const Icon = ({ d, className = 'h-5 w-5' }) => (
  <svg viewBox="0 0 24 24" className={`${className} shrink-0`} fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{d}</svg>
);
const PHONE = <path d="M5 4h4l2 5-2.5 1.5a11 11 0 0 0 5 5L15 13l5 2v4a2 2 0 0 1-2 2A16 16 0 0 1 3 6a2 2 0 0 1 2-2z" />;
const CHAT = <path d="M4 20l1.5-4A8 8 0 1 1 9 19z" />;
const MAIL = <><rect x="3" y="5" width="18" height="14" rx="2" /><path d="M3 7l9 6 9-6" /></>;
const TOOL = <><path d="M14.5 5.5a4 4 0 0 0 4.9 4.9L21 12l-9 9-3-3 9-9z" /><path d="M3 21l6-6" /></>;
const PIN = <><path d="M12 21s-7-6.2-7-11.5A7 7 0 0 1 19 9.5C19 14.8 12 21 12 21z" /><circle cx="12" cy="9.5" r="2.5" /></>;
const CLOCK = <><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></>;

// Counts the call as a Google Ads phone conversion, exactly like the header.
const callSales = (e) => {
  track(FUNNEL.PHONE_CLICK, { source: 'contact_page' });
  if (window.gtagSendEvent) {
    e.preventDefault();
    window.gtagSendEvent(`tel:${SALES.tel}`);
  }
};

function Action({ href, onClick, icon, title, sub, tone, external }) {
  return (
    <a href={href} onClick={onClick}
      {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
      className={`flex min-h-[64px] items-center gap-3 rounded-xl border-[1.5px] bg-white px-3.5 py-2.5 transition hover:shadow-sm ${tone.border}`}>
      <span className={`flex h-10 w-10 items-center justify-center rounded-lg bg-gray-100 ${tone.icon}`}><Icon d={icon} /></span>
      <span className="min-w-0 flex-1">
        <span className="block text-[15.5px] font-bold text-gray-900">{title}</span>
        <span className="block text-[13px] text-gray-600">{sub}</span>
      </span>
    </a>
  );
}

export default function ContactPage() {
  const [form, setForm] = useState({ name: '', mobile: '', need: '', message: '' });
  const [touched, setTouched] = useState(false);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const errors = {
    name: form.name.trim().length < 2 ? 'Enter your name' : null,
    mobile: /^[6-9]\d{9}$/.test(form.mobile) ? null : 'Enter a 10-digit mobile number',
  };

  const submit = async (e) => {
    e.preventDefault();
    setTouched(true);
    if (errors.name || errors.mobile || sending) return;
    setSending(true);
    if (window.gtag) window.gtag('event', 'conversion_event_contact', { send_to: 'AW-17963167817' });
    track(FUNNEL.CONTACT_SUBMITTED || 'contact_submitted', { need: form.need || 'unspecified' });
    const body = new FormData();
    body.append('entry.663475068', form.name.trim());
    body.append('entry.1351859543', form.mobile);
    body.append('entry.266053166', '');
    body.append('entry.377423970', `[Contact page] Need: ${form.need || 'not given'} | ${form.message.trim()}`);
    try {
      await fetch(FORM_URL, { method: 'POST', body, mode: 'no-cors' });
      setSent(true);
      toast.success('Thank you, we have your message', { description: 'We will call you back the same working day.' });
    } catch {
      toast.error('That did not go through', { description: `Please call ${SALES.text} or WhatsApp us.` });
    } finally {
      setSending(false);
    }
  };

  const wa = `https://wa.me/${BUSINESS.whatsapp}?text=${encodeURIComponent('Hi Citi Cool, I have a question.')}`;
  const directions = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(MAP_QUERY)}`;
  const input = (bad) => `mt-1.5 block h-[50px] w-full rounded-xl border bg-white px-3.5 text-base outline-none ${bad ? 'border-red-400' : 'border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500'}`;

  return (
    <>
      <Helmet>
        <title>Contact Citi Cool | Call 77039 00307 | South Extension, New Delhi</title>
        <meta name="description" content="Call 77039 00307 for Daikin, O-General and Hitachi ACs and VRV systems, or 95990 83770 for service and repairs. Showroom at South Extension Part 2, New Delhi. Cooling Delhi since 1990." />
      </Helmet>

      <main className="mx-auto w-full max-w-5xl px-4 pb-16 pt-24 lg:pt-28">
        <h1 className="text-2xl font-extrabold text-gray-900 lg:text-4xl">Talk to Citi Cool</h1>
        <p className="mt-2 max-w-2xl text-[15px] leading-relaxed text-gray-600">
          Cooling Delhi since 1990. Call, WhatsApp or visit us, Monday to Saturday.
        </p>

        <div className="mt-6 grid gap-4 lg:grid-cols-2">
          <div className="space-y-4">
            <section className="rounded-2xl border border-gray-200 bg-white p-4">
              <h2 className="mb-3 text-[17px] font-bold text-gray-900">Buying an AC or a VRV system</h2>
              <div className="space-y-2.5">
                <Action href={`tel:${SALES.tel}`} onClick={callSales} icon={PHONE} title="Call sales" sub={SALES.text}
                  tone={{ border: 'border-blue-200', icon: 'text-blue-700' }} />
                <Action href={wa} external icon={CHAT} title="WhatsApp us" sub="Usually replies within the hour"
                  onClick={() => track(FUNNEL.WHATSAPP_HANDOFF, { source: 'contact_page' })}
                  tone={{ border: 'border-green-200', icon: 'text-green-700' }} />
                <Action href={`mailto:${EMAIL}`} icon={MAIL} title="Email" sub={EMAIL}
                  tone={{ border: 'border-gray-200', icon: 'text-gray-600' }} />
              </div>
            </section>

            <section className="rounded-2xl border border-gray-200 bg-white p-4">
              <h2 className="text-[17px] font-bold text-gray-900">Service and repairs</h2>
              <p className="mb-3 mt-1 text-[13.5px] text-gray-600">For an AC we installed or service, or an AMC visit.</p>
              <Action href={`tel:${SERVICE.tel}`} icon={TOOL} title="Service & repairs" sub={SERVICE.text}
                onClick={() => track(FUNNEL.PHONE_CLICK, { source: 'contact_page_service' })}
                tone={{ border: 'border-amber-200', icon: 'text-amber-700' }} />
            </section>

            <section className="rounded-2xl border border-gray-200 bg-white p-4">
              <h2 className="mb-3 text-[17px] font-bold text-gray-900">Visit our showroom</h2>
              <iframe
                title="Citi Cool showroom on Google Maps"
                src={`https://www.google.com/maps?q=${encodeURIComponent(MAP_QUERY)}&output=embed`}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                className="h-48 w-full rounded-xl border-0 bg-gray-100"
              />
              <p className="mt-3 flex gap-2.5 text-sm leading-relaxed text-gray-900">
                <Icon d={PIN} className="mt-0.5 h-[18px] w-[18px] text-blue-700" />
                <span>{ADDRESS.map((l) => <span key={l} className="block">{l}</span>)}</span>
              </p>
              <p className="mt-2 flex gap-2.5 text-sm text-gray-900">
                <Icon d={CLOCK} className="mt-0.5 h-[18px] w-[18px] text-blue-700" />
                Monday to Saturday, 9 am to 6 pm
              </p>
              <a href={directions} target="_blank" rel="noopener noreferrer"
                className="mt-3 flex min-h-[46px] items-center justify-center rounded-xl border-[1.5px] border-gray-300 text-[14.5px] font-bold text-gray-900 hover:border-gray-400">
                Get directions
              </a>
            </section>
          </div>

          <section className="self-start rounded-2xl border border-gray-200 bg-white p-4">
            <h2 className="text-[17px] font-bold text-gray-900">Send us a message</h2>
            <p className="mb-4 mt-1 text-[13.5px] text-gray-600">We call you back the same working day.</p>
            {sent ? (
              <div className="rounded-xl bg-green-50 p-4 text-sm text-green-800" role="status">
                Thank you, {form.name.split(' ')[0]}. We will call you on {form.mobile}. For anything urgent, call {SALES.text}.
              </div>
            ) : (
              <form onSubmit={submit} noValidate className="space-y-4">
                <label className="block">
                  <span className="text-[13px] font-semibold text-gray-700">Your name</span>
                  <input value={form.name} autoComplete="name" onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                    className={input(touched && errors.name)} />
                  {touched && errors.name && <span className="mt-1 block text-[12px] text-red-600">{errors.name}</span>}
                </label>
                <label className="block">
                  <span className="text-[13px] font-semibold text-gray-700">Mobile number</span>
                  <input value={form.mobile} type="tel" inputMode="numeric" autoComplete="tel-national" maxLength={10}
                    placeholder="10-digit mobile"
                    onChange={(e) => setForm((f) => ({ ...f, mobile: e.target.value.replace(/\D/g, '').slice(0, 10) }))}
                    className={input(touched && errors.mobile)} />
                  {touched && errors.mobile && <span className="mt-1 block text-[12px] text-red-600">{errors.mobile}</span>}
                </label>
                <div>
                  <p className="text-[13px] font-semibold text-gray-700">What do you need?</p>
                  <div className="mt-1.5 flex flex-wrap gap-2">
                    {NEEDS.map((n) => (
                      <button key={n} type="button" aria-pressed={form.need === n}
                        onClick={() => setForm((f) => ({ ...f, need: f.need === n ? '' : n }))}
                        className={form.need === n
                          ? 'min-h-[44px] rounded-xl border-2 border-blue-600 bg-blue-50 px-3.5 text-sm font-semibold text-blue-800'
                          : 'min-h-[44px] rounded-xl border border-gray-300 bg-white px-3.5 text-sm text-gray-700 hover:border-gray-400'}>
                        {n}
                      </button>
                    ))}
                  </div>
                </div>
                <label className="block">
                  <span className="text-[13px] font-semibold text-gray-700">Message <span className="font-normal text-gray-500">(optional)</span></span>
                  <textarea value={form.message} rows={4} placeholder="Rooms, floor area, anything we should know"
                    onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))}
                    className="mt-1.5 block w-full rounded-xl border border-gray-300 px-3.5 py-3 text-base outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500" />
                </label>
                <button type="submit" disabled={sending}
                  className="h-[54px] w-full rounded-xl bg-blue-600 text-base font-bold text-white transition hover:bg-blue-700 disabled:opacity-60">
                  {sending ? 'Sending…' : 'Send'}
                </button>
                <p className="text-center text-[12px] text-gray-500">
                  By sending this you agree to our <a href="/privacy-policy" className="text-blue-700 underline">privacy policy</a>.
                </p>
              </form>
            )}
          </section>
        </div>
      </main>
    </>
  );
}
